import {FC} from 'react'
import {PrismicRichText} from '@prismicio/react'
import {Gradient} from './components/separators'
import {ProductOverviewSliceHighlights} from 'prismicio-types'
import {Container} from 'src/common/Container'
import {isFilled} from '@prismicio/client'

/**
 * Props for `ProductOverview`.
 */
export type ProductOverviewHighlightsVariationProps = {
  slice: ProductOverviewSliceHighlights & {
    slice_type: string
    variation: string
  }
}

/**
 * Component for "ProductOverview" Slices.
 */
export const ProductOverviewHighlightsVariation: FC<ProductOverviewHighlightsVariationProps> = ({slice}) => {
  return (
    <Container
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="flex flex-col items-center gap-6 !pb-0 !pt-10 md:!pt-12 lg:!pt-20"
      backgroundColor="Dark Green"
    >
      <div className="flex w-full max-w-[1200px] flex-col gap-6 px-5 md:px-10 lg:px-5 xl:px-0">
        {isFilled.richText(slice.primary.tag) && (
          <PrismicRichText
            field={slice.primary.tag}
            components={{
              paragraph: ({children}) => <p className="text-button-s text-green-neon">{children}</p>,
            }}
          />
        )}
        {isFilled.richText(slice.primary.heading) && (
          <PrismicRichText
            field={slice.primary.heading}
            components={{
              heading1: ({children}) => (
                <h1 className="md:text-tg-h2 text-32 font-normal leading-[normal] tracking-[-.32px] text-moss-white">
                  {children}
                </h1>
              ),
              strong: ({children}) => <span className="text-moss-white/[70%]">{children}</span>,
            }}
          />
        )}
      </div>
      <div
        className="
        relative
        isolate
        grid
        w-full
        grid-cols-[20px_1fr_20px]
        grid-rows-[32px_repeat(6,auto)_32px]
        md:grid-cols-[40px_minmax(341px,auto)_minmax(341px,auto)_40px]
        md:grid-rows-[64px_repeat(3,auto)_64px]
        lg:grid-cols-[20px_minmax(0,401px)_minmax(0,401px)_minmax(0,401px)_20px]
        lg:grid-rows-[64px_repeat(2,218px)_64px]
        xl:grid-cols-[1fr_minmax(0,401px)_minmax(0,401px)_minmax(0,401px)_1fr]
      "
      >
        <Gradient color="0, 36, 20" gradientStart={0} height={24} location="top" />
        {slice.primary.highlights.map((highlight, index) => (
          <div
            key={`highlight-${index}`}
            className="col-span-1 ml-[-1px] mt-[-1px] flex flex-col gap-4 rounded-[4px] border border-green-light p-6 md:justify-between"
          >
            <p className="text-tg-h2 leading-[normal] text-green-neon lg:text-[72px] lg:leading-[64px] lg:tracking-[-.72px]">
              {highlight.highlight}
            </p>
            <p className="text-detail text-moss-white">{highlight.description}</p>
          </div>
        ))}
        <SideSeparators />
        <CentralSeparators />
        <Gradient color="0, 36, 20" gradientStart={20} height={32} location="bottom" />
      </div>
    </Container>
  )
}

const SideSeparators = () => (
  <>
    {/* Left Side Separators */}
    <div className="col-start-1 row-start-1 rounded-br-[4px] border-b border-r border-green-light" />
    <div className="col-start-1 row-start-2 mt-[-1px] rounded-r-[4px] border-y border-r border-green-light" />
    <div className="col-start-1 row-start-3 mt-[-1px] rounded-r-[4px] border-y border-r border-green-light" />
    <div className="col-start-1 row-start-4 mt-[-1px] rounded-r-[4px] border-y border-r border-green-light lg:hidden" />
    <div className="col-start-1 row-start-5 mt-[-1px] rounded-r-[4px] border-y border-r border-green-light md:hidden" />
    <div className="col-start-1 row-start-6 mt-[-1px] rounded-r-[4px] border-y border-r border-green-light md:hidden" />
    <div className="col-start-1 row-start-7 mt-[-1px] rounded-r-[4px] border-y border-r border-green-light md:hidden" />
    <div className="col-start-1 row-start-8 mt-[-1px] rounded-tr-[4px] border-r border-t border-green-light md:row-start-5 lg:row-start-4" />

    {/* Right Side Separators */}
    <div className="col-start-3 row-start-1 ml-[-1px] rounded-bl-[4px] border-b border-l border-green-light md:col-start-4 lg:col-start-5" />
    <div className="col-start-3 row-start-2 ml-[-1px] mt-[-1px] rounded-l-[4px] border-y border-l border-green-light md:col-start-4 lg:col-start-5" />
    <div className="col-start-3 row-start-3 ml-[-1px] mt-[-1px] rounded-l-[4px] border-y border-l border-green-light md:col-start-4 lg:col-start-5" />
    <div className="col-start-3 row-start-4 ml-[-1px] mt-[-1px] rounded-l-[4px] border-y border-l border-green-light md:col-start-4 lg:hidden" />
    <div className="col-start-3 row-start-5 ml-[-1px] mt-[-1px] rounded-l-[4px] border-y border-l border-green-light md:hidden" />
    <div className="col-start-3 row-start-6 ml-[-1px] mt-[-1px] rounded-l-[4px] border-y border-l border-green-light md:hidden" />
    <div className="col-start-3 row-start-7 ml-[-1px] mt-[-1px] rounded-l-[4px] border-y border-l border-green-light md:hidden" />
    <div className="col-start-3 row-start-8 ml-[-1px] mt-[-1px] rounded-tl-[4px] border-l border-t border-green-light md:col-start-4 md:row-start-5 lg:col-start-5 lg:row-start-4" />
  </>
)

const CentralSeparators = () => (
  <>
    <div className="col-start-2 row-start-1 ml-[-1px] rounded-b-[4px] border-x border-b border-green-light" />
    <div className="col-start-3 row-start-1 ml-[-1px] rounded-b-[4px] border-x border-b border-green-light max-md:hidden" />
    <div className="col-start-4 row-start-1 ml-[-1px] rounded-b-[4px] border-x border-b border-green-light max-lg:hidden" />
    <div className="col-start-2 row-start-8 ml-[-1px] mt-[-1px] rounded-t-[4px] border-x border-t border-green-light md:row-start-5 lg:row-start-4" />
    <div className="col-start-3 row-start-8 ml-[-1px] mt-[-1px] rounded-t-[4px] border-x border-t border-green-light max-md:hidden md:row-start-5 lg:row-start-4" />
    <div className="col-start-4 row-start-8 ml-[-1px] mt-[-1px] rounded-t-[4px] border-x border-t border-green-light max-lg:hidden md:row-start-5 lg:row-start-4" />
  </>
)
